<template>
  <div class="relative">
    <Button
      variant="outline"
      :disabled="loading || disabled"
      :style="{ width: width, maxWidth: maxWidth }"
      class="h-9 inline-flex w-full justify-center items-center px-3 text-sm font-medium min-w-0"
      @click="isOpen = !isOpen">
      <Filter
        :class="['h-4 w-4 mr-1', hasSelection ? 'text-primary' : '']"
        :fill="hasSelection ? 'currentColor' : 'none'" />
      <span class="px-1">
        {{ selectionText }}
      </span>
      <ChevronDown class="-mr-1 ml-2 h-5 w-5" :class="isOpen ? 'rotate-180' : ''" />
    </Button>
    
    <!-- Dropdown Content -->
    <div v-if="isOpen" class="absolute top-full left-0 right-0 mt-1 z-50 min-w-80 bg-popover border border-border rounded-md shadow-md max-h-80 overflow-auto">
      <div class="p-3">
        <!-- Clear All Button -->
        <div v-if="hasSelection" class="pb-2 mb-2 border-b border-border">
          <Button variant="ghost" size="sm" class="w-full text-left justify-start h-8 px-2" @click="clearAll">
            <span class="text-xs">Clear All Selections</span>
          </Button>
        </div>
        
        <!-- Sources Section -->
        <Collapsible v-model:open="sourcesOpen" class="w-full">
          <CollapsibleTrigger as-child>
            <Button variant="ghost" class="w-full justify-between p-2 h-8 rounded-sm hover:bg-accent hover:text-accent-foreground">
              <div class="flex items-center gap-2">
                <CloudDownload class="h-4 w-4 text-muted-foreground" />
                <span class="font-medium text-sm">Sources</span>
                <Badge v-if="selectedSources.length" variant="secondary" class="ml-1 text-xs">
                  {{ selectedSources.length }}
                </Badge>
              </div>
              <ChevronRight class="h-3 w-3 text-muted-foreground transition-transform duration-200"
                           :class="sourcesOpen ? 'rotate-90' : ''" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent class="pl-4 mt-1">
            <div class="space-y-1.5">
              <div v-for="source in sourceOptions" :key="source.value" class="flex items-center space-x-2 py-1 px-2 rounded-sm hover:bg-accent/50">
                <Checkbox
                  :id="`source-${source.value}`"
                  :checked="selectedSources.includes(source.value)"
                  class="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  @click="() => toggleSource(source.value, !selectedSources.includes(source.value))" />
                <label :for="`source-${source.value}`" class="text-sm cursor-pointer flex items-center gap-2 flex-1 min-w-0"
                       @click="() => toggleSource(source.value, !selectedSources.includes(source.value))">
                  <Avatar v-if="source.connectorType" class="w-4 h-4 bg-muted border border-border rounded-sm">
                    <AvatarImage :src="`/icons/${source.connectorType}.svg`" :alt="source.label" class="object-contain p-0.5" />
                    <AvatarFallback class="bg-muted text-muted-foreground text-xs rounded-sm">
                      {{ source.connectorType?.charAt(0).toUpperCase() }}
                    </AvatarFallback>
                  </Avatar>
                  <span class="truncate text-sm">{{ source.label }}</span>
                </label>
              </div>
              <div v-if="!sourceOptions.length" class="text-xs text-muted-foreground py-2 px-2">
                No sources available
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        <!-- Destinations Section -->
        <Collapsible v-model:open="destinationsOpen" class="w-full mt-1">
          <CollapsibleTrigger as-child>
            <Button variant="ghost" class="w-full justify-between p-2 h-8 rounded-sm hover:bg-accent hover:text-accent-foreground">
              <div class="flex items-center gap-2">
                <CloudUpload class="h-4 w-4 text-muted-foreground" />
                <span class="font-medium text-sm">Destinations</span>
                <Badge v-if="selectedDestinations.length" variant="secondary" class="ml-1 text-xs">
                  {{ selectedDestinations.length }}
                </Badge>
              </div>
              <ChevronRight class="h-3 w-3 text-muted-foreground transition-transform duration-200"
                           :class="destinationsOpen ? 'rotate-90' : ''" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent class="pl-4 mt-1">
            <div class="space-y-1.5">
              <div v-for="destination in destinationOptions" :key="destination.value" class="flex items-center space-x-2 py-1 px-2 rounded-sm hover:bg-accent/50">
                <Checkbox
                  :id="`destination-${destination.value}`"
                  :checked="selectedDestinations.includes(destination.value)"
                  class="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  @click="() => toggleDestination(destination.value, !selectedDestinations.includes(destination.value))" />
                <label :for="`destination-${destination.value}`" class="text-sm cursor-pointer flex items-center gap-2 flex-1 min-w-0"
                       @click="() => toggleDestination(destination.value, !selectedDestinations.includes(destination.value))">
                  <Avatar v-if="destination.connectorType" class="w-4 h-4 bg-muted border border-border rounded-sm">
                    <AvatarImage :src="`/icons/${destination.connectorType}.svg`" :alt="destination.label" class="object-contain p-0.5" />
                    <AvatarFallback class="bg-muted text-muted-foreground text-xs rounded-sm">
                      {{ destination.connectorType?.charAt(0).toUpperCase() }}
                    </AvatarFallback>
                  </Avatar>
                  <span class="truncate text-sm">{{ destination.label }}</span>
                </label>
              </div>
              <div v-if="!destinationOptions.length" class="text-xs text-muted-foreground py-2 px-2">
                No destinations available
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
    
    <!-- Backdrop to close dropdown -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="isOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import { Filter, ChevronDown, ChevronRight, CloudDownload, CloudUpload } from 'lucide-vue-next'

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({ sources: [], destinations: [] })
    },
    sources: {
        type: Array,
        default: () => []
    },
    destinations: {
        type: Array,
        default: () => []
    },
    placeholder: {
        type: String,
        default: 'Select connectors...'
    },
    loading: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    width: {
        type: String,
        default: 'auto'
    },
    maxWidth: {
        type: String,
        default: '18rem'
    }
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const sourcesOpen = ref(false)
const destinationsOpen = ref(false)

const sourceOptions = computed(() => {
    if (!props.sources?.length) return []
  
    return props.sources.map(source => {
        if (typeof source === 'string') {
            return { value: source, label: source }
        }
    
        return {
            value: source.id || source.value || source.name,
            label: source.name || source.label || source.displayValue || source.id,
            connectorType: source.connectorType || source.connector
        }
    })
})

const destinationOptions = computed(() => {
    if (!props.destinations?.length) return []
  
    return props.destinations.map(destination => {
        if (typeof destination === 'string') {
            return { value: destination, label: destination }
        }
    
        return {
            value: destination.id || destination.value || destination.name,
            label: destination.name || destination.label || destination.displayValue || destination.id,
            connectorType: destination.connectorType || destination.connector
        }
    })
})

const selectedSources = computed(() => props.modelValue.sources || [])
const selectedDestinations = computed(() => props.modelValue.destinations || [])

const hasSelection = computed(() => {
    return selectedSources.value.length > 0 || selectedDestinations.value.length > 0
})

const selectionText = computed(() => {
    const sourcesCount = selectedSources.value.length
    const destinationsCount = selectedDestinations.value.length
    const totalSelected = sourcesCount + destinationsCount
  
    if (totalSelected === 0) {
        return props.placeholder
    }
  
    const parts = []
    if (sourcesCount > 0) {
        parts.push(`${sourcesCount} source${sourcesCount === 1 ? '' : 's'}`)
    }
    if (destinationsCount > 0) {
        parts.push(`${destinationsCount} destination${destinationsCount === 1 ? '' : 's'}`)
    }
  
    return parts.join(', ')
})

const toggleSource = (sourceValue, checked) => {
    const newSources = checked
        ? [...selectedSources.value, sourceValue]
        : selectedSources.value.filter(s => s !== sourceValue)
    
    emit('update:modelValue', {
        sources: newSources,
        destinations: selectedDestinations.value
    })
}

const toggleDestination = (destinationValue, checked) => {
    const newDestinations = checked
        ? [...selectedDestinations.value, destinationValue]
        : selectedDestinations.value.filter(d => d !== destinationValue)
    
    emit('update:modelValue', {
        sources: selectedSources.value,
        destinations: newDestinations
    })
}

const clearAll = () => {
    emit('update:modelValue', {
        sources: [],
        destinations: []
    })
}

const handleKeydown = (event) => {
    if (event.key === 'Escape') {
        isOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
})
</script>